// The page state of the v2 listing (HUBX-8479) as pure functions: what each trigger fetches,
// what each request carries, and how the tab strip is read off the counters. It replaces the
// legacy `vendorService.search` (IVendorSearch) as the page's source of truth, and with it
// legacy-listing.adapter, which only translated between the two while the rows were legacy.
//
// No Angular and no `this`, like the write rules (HUBX-8474): items.component holds ONE
// IListingPageState and replaces it through these transitions, so every legacy rule the
// screen keeps is written down once and tested without TestBed.

import { ListingSkuStatusNamesEnum, ListingSkuStatusValuesEnum } from '../../enums/listing-sku-status';
import { IVendorStatus } from '../../interface/IVendorStatus';
import { EffectiveStatusName, IListingFilters, IListingQuery, IStatusCounter, ListingTab } from './item-listing.models';

/** Everything the page needs to ask for a page. The filters are the six controls of the
 *  filter bar plus the search text (`q`), exactly as they travel; the dropdowns' chosen
 *  objects stay in the component, since they are presentation. */
export interface IListingPageState {
  tab: ListingTab;
  filters: IListingFilters;
  sortDirection: 'asc' | 'desc';
  /** 1-based. */
  page: number;
}

/** Legacy opens on Active, most inventory first (items.component getDefaultFilter). */
export function initialListingState(): IListingPageState {
  return { tab: 'Active', filters: {}, sortDirection: 'desc', page: 1 };
}

// ---------------------------------------------------------------------------------------------
// When the client calls what
// ---------------------------------------------------------------------------------------------

export type ListingTrigger = 'page' | 'sort' | 'tab' | 'filter' | 'search' | 'clear' | 'vendor' | 'firstLoad' | 'create' | 'save';

/** Which of the three reads a trigger makes. `page` is `GET /items` through the filters; a
 *  save refills its rows by id instead (HUBX-8488), which the save service does itself. */
export interface IListingFetchPlan {
  page: boolean;
  filterOptions: boolean;
  statusCounts: boolean;
}

/**
 * The table of the design page "Listing › When the client calls what":
 *
 * - paging, sort, tab: the page only. The counters drop exactly the status predicate, and the
 *   filter options take no predicate at all.
 * - filter, search: the page and the counters.
 * - first load, vendor switch, the header click (clear): all three.
 * - create / clone: all three - the new item belongs to the result set and can bring a new
 *   manufacturer, category, ExW... into the catalogue.
 * - save: never the page - refetching it would replace the rows and lose what the save just
 *   did to them - but both aggregates, which a save can move.
 *
 * The filter options are the session vendor's whole catalogue (HUBX-8487, BE 185cbee): no
 * filter, tab, search or page moves them, so they are asked for only when the catalogue can
 * have changed (a write), when it is another vendor's, or when the user reloads the listing.
 */
export function fetchPlan(trigger: ListingTrigger): IListingFetchPlan {
  switch (trigger) {
    case 'page':
    case 'sort':
    case 'tab':
      return { page: true, filterOptions: false, statusCounts: false };
    case 'filter':
    case 'search':
      return { page: true, filterOptions: false, statusCounts: true };
    case 'save':
      return { page: false, filterOptions: true, statusCounts: true };
    default:
      return { page: true, filterOptions: true, statusCounts: true };
  }
}

// ---------------------------------------------------------------------------------------------
// What each request carries
// ---------------------------------------------------------------------------------------------

/** `GET /items`: the filters, the tab by NAME (All is no status), the direction and the page.
 *  There is no orderBy (one sort: inventory) and no pageSize (25 is the contract). */
export function pageQuery(state: IListingPageState): IListingQuery {
  const query: IListingQuery = { ...copyFilters(state.filters), sortDirection: state.sortDirection, page: state.page };
  const status = statusOf(state.tab);
  if (status) { query.status = status; }
  return query;
}

// `GET /items/filter-options` takes no parameters (HUBX-8487): its lists are the vendor's
// whole catalogue, whatever the page shows.

// `GET /items/status-counts` takes `state.filters` as they are: ItemListingService already
// sends the filters only, whatever else it is handed.

// ---------------------------------------------------------------------------------------------
// Transitions — each returns a new state and never mutates the one it is given
// ---------------------------------------------------------------------------------------------

/** A tab keeps the filter bar as it is (the ticket: "A tab change keeps the filter bar"),
 *  keeps the sort, and goes back to page 1 (legacy selectStatusAndRetrieveItems). */
export function withTab(state: IListingPageState, tab: ListingTab): IListingPageState {
  return { ...state, tab, filters: copyFilters(state.filters), page: 1 };
}

/**
 * A filter-bar choice. Legacy parity, unchanged:
 * - it drops the search text (every selector calls clearSearchFilter first);
 * - it lands on ACTIVE, whatever the tab was — getVendorItems forces skuStatus = active when
 *   no search is typed — and the empty-tab fallback may then move it to All (fallbackTab);
 * - it keeps the sort and goes back to page 1.
 * A member patched to `undefined` (a dropdown's "All …") is removed, so it is not sent.
 */
export function withFilters(state: IListingPageState, patch: Partial<IListingFilters>): IListingPageState {
  const filters: IListingFilters = { ...copyFilters(state.filters), ...patch };
  delete filters.q;
  for (const key of Object.keys(filters) as (keyof IListingFilters)[]) {
    if (filters[key] === undefined) { delete filters[key]; }
  }
  return { ...state, tab: 'Active', filters, page: 1 };
}

/**
 * The search box. The design page ("Search clears the filter bar") keeps legacy's rule: a
 * search drops category, manufacturer, promotion, restriction and exw and moves to All — a
 * global search spans every vendor, and a vendor-scoped filter bar would be meaningless
 * there. It also resets the sort to most-inventory-first, as searchItemsByText rebuilt the
 * request with DESC.
 *
 * An empty search is the first-load state (legacy: skuStatus = all, then getVendorItems saw no
 * search text and forced Active), which is also what the header refresh does.
 */
export function withSearch(state: IListingPageState, text: string): IListingPageState {
  const q = text?.trim();
  if (!q) { return initialListingState(); }
  return { tab: 'All', filters: { q }, sortDirection: 'desc', page: 1 };
}

/** The header click (refreshItemsData) and the vendor switch (changeCustomerVendor): nothing
 *  chosen, nothing typed, back on Active. */
export function withoutFilters(): IListingPageState {
  return initialListingState();
}

/** The "Available" button: the one sort, the other direction, from page 1. v2 orders BEFORE
 *  paging, so page 1 is the 25 rows with the most (or least) inventory in the whole result
 *  set — legacy sorted the 25 it had already cut (design page, "Divergence: legacy sorts after
 *  paging, v2 sorts before"). */
export function withSortToggled(state: IListingPageState): IListingPageState {
  return { ...state, filters: copyFilters(state.filters), sortDirection: state.sortDirection === 'asc' ? 'desc' : 'asc', page: 1 };
}

export function withPage(state: IListingPageState, page: number): IListingPageState {
  return { ...state, filters: copyFilters(state.filters), page };
}

// ---------------------------------------------------------------------------------------------
// The tab strip
// ---------------------------------------------------------------------------------------------

/** One tab as the strip renders it. */
export interface IListingTabView {
  tab: ListingTab;
  /** The text on the tab, and the name the dashboard deep link carries. */
  label: string;
  count: number;
  /**
   * The LEGACY SkuStatus ordinal of this bucket. Only the export uses it: the Export Listings
   * checkboxes post these ordinals to the legacy ExportListing endpoint, which is not part of
   * the v2 read surface and is out of this ticket's scope. Never sent to a v2 route — the v2
   * wire ordinal (STATUS_COUNT_CODES) diverges from it after Active.
   */
  exportValue: ListingSkuStatusValuesEnum;
}

/** Today's strip, unchanged: legacy order with Pending moved after Active (HUBX-8464), and
 *  the labels legacy StatusCounts used — Out Of Stock is still shown as "Sold Out". */
const TAB_DISPLAY: readonly { tab: ListingTab; label: string; exportValue: ListingSkuStatusValuesEnum }[] = [
  { tab: 'All', label: 'All', exportValue: ListingSkuStatusValuesEnum.all },
  { tab: 'Active', label: ListingSkuStatusNamesEnum.active, exportValue: ListingSkuStatusValuesEnum.active },
  { tab: 'Pending', label: ListingSkuStatusNamesEnum.pending, exportValue: ListingSkuStatusValuesEnum.pending },
  { tab: 'Inactive', label: ListingSkuStatusNamesEnum.inactive, exportValue: ListingSkuStatusValuesEnum.inactive },
  { tab: 'OutOfStock', label: ListingSkuStatusNamesEnum.soldOut, exportValue: ListingSkuStatusValuesEnum.soldOut },
  { tab: 'Paused', label: ListingSkuStatusNamesEnum.paused, exportValue: ListingSkuStatusValuesEnum.paused },
  { tab: 'Removed', label: ListingSkuStatusNamesEnum.removed, exportValue: ListingSkuStatusValuesEnum.removed },
  { tab: 'Archived', label: ListingSkuStatusNamesEnum.archived, exportValue: ListingSkuStatusValuesEnum.archived }
];

/** The strip straight from `GET /items/status-counts`: the server owns its shape (eight rows,
 *  zeros included), so a tab the server did not send is not synthesised, and nothing about a
 *  count is recomputed here. */
export function tabsForDisplay(counters: readonly IStatusCounter[]): IListingTabView[] {
  const byTab = new Map(counters.map(c => [c.tab, c.count]));
  return TAB_DISPLAY
    .filter(d => byTab.has(d.tab))
    .map(d => ({ ...d, count: byTab.get(d.tab) as number }));
}

/** The dashboard's Listing Details boxes from the same counters as the strip (HUBX-8479), in
 *  the legacy StatusCounts shape it binds to: the strip label as name — the boxes link to
 *  `/vendor/items/status/:name`, which tabFromRouteName reads back — and the legacy ordinal as
 *  value. All stays in: the DashboardService setter splits it off as the total. */
export function dashboardStatusesFromCounters(counters: readonly IStatusCounter[]): IVendorStatus[] {
  return tabsForDisplay(counters).map(t => ({ name: t.label, value: t.exportValue, count: t.count }));
}

/** The selected bucket's count. It IS the page's total — the counters run the page's WHERE
 *  minus the status predicate, and the bucket is that predicate; on All it is the server's sum
 *  — so the pager and the header badge are fed from here, and stay right after a save without
 *  refetching the page (design page, "Pagination"). */
export function countOf(tabs: readonly IListingTabView[], tab: ListingTab): number {
  return tabs.find(t => t.tab === tab)?.count ?? 0;
}

/**
 * Legacy parity (getVendorItemsAndStatusData): after the counters come back, a selected tab
 * with no rows falls back to All — unless the dashboard deep link asked for that tab, in which
 * case the user sees the empty tab they clicked. All itself, and a tab the counters did not
 * answer for, stay.
 */
export function fallbackTab(tabs: readonly IListingTabView[], tab: ListingTab, pinnedByRoute: boolean): ListingTab {
  if (tab === 'All' || pinnedByRoute) { return tab; }
  const view = tabs.find(t => t.tab === tab);
  return view && view.count === 0 ? 'All' : tab;
}

/** The dashboard links to `/vendor/items/status/:statusName` with the tab LABEL ("Sold Out");
 *  the v2 name is read too. Anything else is no tab. */
export function tabFromRouteName(name: string | null | undefined): ListingTab | undefined {
  if (!name) { return undefined; }
  return TAB_DISPLAY.find(d => d.label === name || d.tab === name)?.tab;
}

/** The name written back to that route, so the strip and the dashboard keep agreeing. */
export function routeNameOf(tab: ListingTab): string {
  return TAB_DISPLAY.find(d => d.tab === tab)?.label ?? tab;
}

// ---------------------------------------------------------------------------------------------

function statusOf(tab: ListingTab): EffectiveStatusName | undefined {
  return tab === 'All' ? undefined : tab;
}

/** A copy, so a state handed to a request can never be changed through it. */
function copyFilters(filters: IListingFilters): IListingFilters {
  const copy: IListingFilters = { ...filters };
  if (filters.categoryIds) { copy.categoryIds = [...filters.categoryIds]; }
  return copy;
}
